import { Star, Shield, Clock, Users, Heart, Award, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const About = () => {
  const values = [
    {
      title: 'Honesty',
      description: 'We believe in transparent pricing and honest communication. No hidden fees, no surprises.',
      icon: Heart,
    },
    {
      title: 'Quality',
      description: 'Every job is done right the first time, using quality materials and proven techniques.',
      icon: Award,
    },
    {
      title: 'Reliability',
      description: 'When we say we\'ll be there, we show up. Count on us for prompt, dependable service.',
      icon: Clock,
    },
    {
      title: 'Community',
      description: 'We\'re proud to serve the Wiregrass area we call home. Your neighbors are our neighbors.',
      icon: Users,
    },
  ];

  const stats = [
    { number: '65+', label: 'Years Combined Experience' },
    { number: '1000+', label: 'Happy Customers' },
    { number: '4+', label: 'Star Rating' },
    { number: '9+', label: 'Wiregrass Cities Served' },
  ];

  const serviceAreas = [
    'Dothan', 'Enterprise', 'Ozark', 'Geneva', 'Headland',
    'Midland City', 'Daleville', 'Newton', 'Rehobeth'
  ];

  return (
    <>
      <SEO
        title="About Ard's Plumbing & Electric | Dothan AL"
        description="Learn about Ard's Plumbing & Electric. Family-owned business with 65+ years combined experience serving Dothan and the Wiregrass area. Licensed & insured."
        keywords="about Ard's Plumbing Electric, Dothan plumber history, family owned plumber, Wiregrass plumbing company, Alabama licensed plumber"
        canonical="/about"
        additionalSchema={{ '@context': 'https://schema.org', '@type': 'BreadcrumbList', 'itemListElement': [{ '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' }, { '@type': 'ListItem', 'position': 2, 'name': 'About Us', 'item': 'https://ardsdothan.com/about' }] }}
      />

      {/* Hero Section */}
      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* ACCESSIBILITY: Changed white/20 to black/20 for better contrast against brand-blue */}
              <div className="inline-flex items-center gap-2 bg-black/20 text-white text-sm px-4 py-2 rounded-full mb-6">
                <Heart className="w-4 h-4" />
                Family Owned & Operated
              </div>
              <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
                OUR STORY<br />
                <span className="text-brand-yellow">YOUR TRUST</span>
              </h1>
              <p className="text-lg text-white mb-8 max-w-lg">
                For over 65 years, Ard's Plumbing & Electric has been the trusted choice 
                for plumbing and electrical services in the Wiregrass area.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Work With Us</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-blue transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  (334) 692-5537
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/team-van.jpg"
                alt="Ard's Plumbing and Electric team in Dothan AL"
                className="rounded-[28px] shadow-2xl w-full"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading font-black text-4xl lg:text-5xl text-brand-yellow mb-2">
                  {stat.number}
                </p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-8">
            WHO WE ARE
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              Ard's Plumbing & Electric is a family-owned business built on a simple promise: 
              provide honest, reliable service that our neighbors can count on. For generations, 
              we've served the Wiregrass area with pride, treating every home and business as if it were our own.
            </p>
            <p>
              Our team brings together over 65 years of combined experience in plumbing and electrical work. 
              From routine repairs to complex installations, we have the expertise to handle any job—big or small.
            </p>
            <p>
              What sets us apart? We show up when we say we will. We explain everything clearly. 
              We stand behind our work with a 90-day workmanship guarantee. And we treat our customers 
              with the respect they deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              OUR VALUES
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-shadow"
              >
                <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-brand-blue" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-black mb-2">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 px-4 bg-brand-yellow">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl lg:text-5xl text-brand-black mb-4">
              SERVICE AREAS
            </h2>
            <p className="text-lg text-brand-black/70 max-w-2xl mx-auto">
              Proudly serving the Wiregrass area and surrounding communities.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((city, index) => (
              <Link
                key={index}
                /* NEW URL FORMAT: service-areas/city-al */
                to={`/service-areas/${city.toLowerCase().replace(' ', '-')}-al`}
                className="flex items-center gap-2 bg-white text-brand-black px-6 py-3 rounded-full font-medium hover:bg-brand-navy hover:text-white transition-colors"
              >
                <MapPin className="w-4 h-4" />
                {city}, AL
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-20 px-4 bg-brand-navy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl lg:text-5xl text-white mb-6">
                WHY WORK<br />
                <span className="text-brand-yellow">WITH US?</span>
              </h2>
              <p className="text-lg text-white/80 mb-8">
                When you choose Ard's Plumbing & Electric, you're choosing a partner 
                who values your time, your property, and your trust.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Shield className="w-6 h-6 text-brand-yellow" />
                  </div>
                  <div>
                    <p className="font-semibold">Licensed & Insured</p>
                    <p className="text-white/70 text-sm">Full coverage for your protection</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-brand-yellow" />
                  </div>
                  <div>
                    <p className="font-semibold">4+ Star Rated</p>
                    <p className="text-white/70 text-sm">Consistently excellent reviews</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-white">
                  <div className="w-12 h-12 bg-brand-yellow/20 rounded-full flex items-center justify-center">
                    <Clock className="w-6 h-6 text-brand-yellow" />
                  </div>
                  <div>
                    <p className="font-semibold">Mon-Fri 8AM-5PM</p>
                    <p className="text-white/70 text-sm">Fast response when you need us</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-3xl p-8">
              <h3 className="font-heading font-bold text-2xl text-white mb-6">
                Ready to Get Started?
              </h3>
              <p className="text-white/80 mb-8">
                Contact us today to schedule your plumbing or electrical service. 
                We're here to help with all your home and business needs.
              </p>
              <div className="flex flex-col gap-4">
                <Button
                  asChild
                  className="bg-brand-coral hover:bg-brand-coral/90 text-white rounded-full px-8 py-6 text-lg font-semibold"
                >
                  <Link to="/contact-us">Book Online</Link>
                </Button>
                <a
                  href="tel:3346925537"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-brand-navy transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Call (334) 692-5537
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;